"use client";

import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  MobileBottomToolClickContext,
  MobileBottomToolId,
  MobileBottomToolItem,
  MobileBottomToolTone,
} from "./types";
import { MOBILE_BOTTOM_TOOL_DOCK_HEIGHT } from "./constants";

interface MobileBottomToolDockProps {
  tools: MobileBottomToolItem[];
  selectedToolId?: MobileBottomToolId | null;
  openToolId?: MobileBottomToolId | null;
  label?: string;
  mobileOnly?: boolean;
  className?: string;
  onToolPress?: (
    tool: MobileBottomToolItem,
    context: MobileBottomToolClickContext,
  ) => void | Promise<void>;
  closeSheet: () => void;
  openSheet: (id: MobileBottomToolId) => void;
  toggleSheet: (id: MobileBottomToolId) => void;
}

function toolIsBusy(tool: MobileBottomToolItem): boolean {
  return Boolean(tool.loading || tool.pending);
}

function toolIsSelected(
  tool: MobileBottomToolItem,
  selectedToolId: MobileBottomToolId | null | undefined,
  openToolId: MobileBottomToolId | null | undefined,
): boolean {
  if (tool.selected !== undefined) return tool.selected;
  if (tool.kind === "view") return selectedToolId === tool.id;
  if (tool.kind === "panel" || tool.kind === "status-panel") {
    return openToolId === tool.id;
  }
  return false;
}

const TOOL_TONE_STYLES: Record<
  MobileBottomToolTone,
  {
    focusRing: string;
    selectedButton: string;
    restingButton: string;
    selectedStatus: string;
    restingStatus: string;
  }
> = {
  amber: {
    focusRing: "focus-visible:ring-amber-500",
    selectedButton:
      "border-transparent bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
    restingButton:
      "border-transparent text-muted-foreground hover:bg-accent hover:text-foreground",
    selectedStatus:
      "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950",
    restingStatus: "bg-muted text-muted-foreground",
  },
  emerald: {
    focusRing: "focus-visible:ring-emerald-500",
    selectedButton:
      "border-emerald-600 bg-emerald-500 text-white hover:bg-emerald-600 dark:border-emerald-500 dark:bg-emerald-600 dark:hover:bg-emerald-700",
    restingButton:
      "border-emerald-300 bg-emerald-50/70 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-100/80 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50",
    selectedStatus:
      "bg-white/90 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-200",
    restingStatus:
      "bg-white text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  },
};

export function MobileBottomToolDock({
  tools,
  selectedToolId,
  openToolId,
  label = "페이지 도구",
  mobileOnly = true,
  className,
  onToolPress,
  closeSheet,
  openSheet,
  toggleSheet,
}: MobileBottomToolDockProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const visibleTools = React.useMemo(
    () =>
      tools
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter((tool) => tool.label.trim().length > 0),
    [tools],
  );

  const compactScroll = visibleTools.length > 6;
  const gridTemplateColumns = compactScroll
    ? `repeat(${visibleTools.length}, minmax(3.5rem, 1fr))`
    : `repeat(${visibleTools.length}, minmax(0, 1fr))`;

  const updateScrollHint = React.useCallback(() => {
    const node = scrollRef.current;
    if (!node || !compactScroll) {
      setCanScrollRight(false);
      return;
    }

    setCanScrollRight(
      node.scrollLeft + node.clientWidth < node.scrollWidth - 1,
    );
  }, [compactScroll]);

  React.useEffect(() => {
    updateScrollHint();
  }, [updateScrollHint, visibleTools.length]);

  React.useEffect(() => {
    const node = scrollRef.current;
    if (!node || !compactScroll) return;

    node.addEventListener("scroll", updateScrollHint, { passive: true });
    window.addEventListener("resize", updateScrollHint);
    return () => {
      node.removeEventListener("scroll", updateScrollHint);
      window.removeEventListener("resize", updateScrollHint);
    };
  }, [compactScroll, updateScrollHint]);

  if (visibleTools.length === 0) return null;

  return (
    <nav
      aria-label={label}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 [visibility:var(--mobile-tool-dock-visibility,visible)] border-t border-border bg-background/95 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-background/85",
        // 클래식 레이아웃 전 구간(<1024px, 셸 게이트와 일치)에서 노출
        mobileOnly && "lg:hidden",
        className,
      )}
      style={{
        "--mobile-bottom-tool-dock-height": MOBILE_BOTTOM_TOOL_DOCK_HEIGHT,
        paddingBottom: "env(safe-area-inset-bottom)",
      } as React.CSSProperties}
    >
      <div className="relative mx-auto w-full max-w-md">
        <div
          ref={scrollRef}
          onScroll={updateScrollHint}
          className={cn(
            "h-[var(--mobile-bottom-tool-dock-height)] px-2 py-1.5",
            compactScroll &&
              "overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
        >
          <div
            className="grid h-full gap-1"
            style={{
              gridTemplateColumns,
              minWidth: compactScroll
                ? `${visibleTools.length * 56}px`
                : undefined,
            }}
          >
            {visibleTools.map((tool) => {
              const Icon = tool.icon;
              const busy = toolIsBusy(tool);
              const disabled = Boolean(tool.disabled || busy);
              const selected = toolIsSelected(tool, selectedToolId, openToolId);
              const hasSheet =
                (tool.kind === "panel" || tool.kind === "status-panel") &&
                Boolean(tool.sheet);
              const controlsId = hasSheet
                ? `mobile-bottom-tool-sheet-${tool.id}`
                : undefined;
              const toneStyles = TOOL_TONE_STYLES[tool.tone ?? "amber"];
              const itemClassName = cn(
                "relative flex min-h-12 min-w-0 touch-manipulation flex-col items-center justify-center gap-0.5 rounded-md border px-1.5 py-1 text-center text-[11px] font-medium leading-none transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                toneStyles.focusRing,
                selected ? toneStyles.selectedButton : toneStyles.restingButton,
                disabled && "cursor-not-allowed opacity-50",
                tool.kind === "status" && "cursor-default",
              );
              const toolContent = (
                <>
                  {tool.badge && (
                    <span className="pointer-events-none absolute right-0.5 top-0.5 z-10 flex items-center justify-center">
                      {tool.badge}
                    </span>
                  )}
                  {busy ? (
                    <Loader2
                      className="h-5 w-5 shrink-0 animate-spin"
                      aria-hidden
                    />
                  ) : tool.renderIcon ? (
                    tool.renderIcon({
                      className: "h-5 w-5 shrink-0",
                      selected,
                      disabled,
                      busy,
                    })
                  ) : Icon ? (
                    <Icon className="h-5 w-5 shrink-0" aria-hidden />
                  ) : (
                    <span className="h-5 w-5 shrink-0" aria-hidden />
                  )}
                  {!tool.hideLabel && (
                    <span className="block max-w-full truncate">
                      {tool.label}
                    </span>
                  )}
                  {tool.status && (
                    <span
                      className={cn(
                        "mt-0.5 block max-w-full truncate rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                        selected
                          ? toneStyles.selectedStatus
                          : toneStyles.restingStatus,
                      )}
                    >
                      {tool.status}
                    </span>
                  )}
                </>
              );

              if (tool.kind === "status") {
                return (
                  <div
                    key={tool.id}
                    role="status"
                    aria-label={tool.ariaLabel ?? tool.label}
                    title={tool.ariaLabel ?? tool.label}
                    className={itemClassName}
                  >
                    {toolContent}
                  </div>
                );
              }

              return (
                <button
                  key={tool.id}
                  type="button"
                  disabled={disabled}
                  aria-label={tool.ariaLabel ?? tool.label}
                  aria-pressed={tool.kind === "view" ? selected : undefined}
                  aria-expanded={hasSheet ? openToolId === tool.id : undefined}
                  aria-controls={controlsId}
                  aria-busy={busy || undefined}
                  title={tool.ariaLabel ?? tool.label}
                  onClick={() => {
                    void onToolPress?.(tool, {
                      closeSheet,
                      openSheet,
                      toggleSheet,
                    });
                  }}
                  className={itemClassName}
                >
                  {toolContent}
                </button>
              );
            })}
          </div>
        </div>
        {compactScroll && canScrollRight && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-1.5 right-0 w-10 bg-gradient-to-l from-background via-background/90 to-transparent"
          />
        )}
      </div>
    </nav>
  );
}
